/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState } from 'react';

import { Button, CardContent, Card, CardHeader, Divider, AlertCircleIcon, ChevronUpIcon, ChevronDownIcon } from "@forge/common";

import OverviewTable from './OverviewTable';

import "./styles.scss";

interface DeviceData {
	[key: string]: string | number | boolean | undefined | null | [{
		[key: string]: string | number | boolean | undefined | null;
	}];
}

interface DataCardProps {
	deviceData: DeviceData[];
	title: string;
	titleBadge?: React.ReactNode;
	subtitle?: string;
	onClick?: () => void;
	buttonTitle?: string;
	isButtonVisible?: boolean;
	isCollapsible?: boolean;
	defaultExpanded?: boolean;
}

export const DataCardTable: React.FC<DataCardProps> = ({ deviceData, title, titleBadge, subtitle, onClick, buttonTitle, isButtonVisible, isCollapsible = false, defaultExpanded = true }) => {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const getSelectedData = (data: any) => {
		return data?.split(", ").map((item: string, index: number) => {
			return { id: index, name: item };
		});
	};

	const renderValuesComp = (data: DeviceData) => {
		if (data.type === "site") {
			return <OverviewTable title={(data.label || "").toString()} selectedData={getSelectedData(data.value)} />;
		} else if (Array.isArray(data.value)) {
			return data.value
				.map((item) => Object.entries(item)
					.map(([key, value]) => `${value}`)
					.join(', '))
				.join(', ');
		} else {
			return String(data.value);
		}
	};

	const renderLabelComp = (data: DeviceData) => {
		if (data.type === "site") {
			return "";
		} else {			
			return (
				<div className="reusable-info-label">{String(data.label)}
					{data.isAlertIconVisible && <AlertCircleIcon className='data-card-alert-circle-icon' />}
				</div>
			);
		}
	};

	return (
		<Card className="mx-auto min-w-full max-w-4xl reusable-tab-card-container">
			<div className='card-header-container'>
				<div
					className={`card-header-wrapper ${isCollapsible ? 'collapsible-header' : ''}`}
					onClick={isCollapsible ? () => setIsExpanded(!isExpanded) : undefined}
					style={isCollapsible ? { cursor: 'pointer' } : undefined}
				>
					<div className="card-header-with-badge">
						<CardHeader title={title} subtitle={subtitle} className="card-header-title" />
						{titleBadge && (
							<div className="card-header-badge">
								{titleBadge}
							</div>
						)}
					</div>
					{isCollapsible && (
						<div className="chevron-icon">
							{isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
						</div>
					)}
				</div>
				{isButtonVisible && <Button
					iconPosition="left"
					onClick={onClick}
					size="regular"
					variant="primary"
					className='data-card-button'
					aria-label='custombutton'
				>
					{buttonTitle}
				</Button>}
			</div>
			{(!isCollapsible || isExpanded) && (
				<CardContent className='data-card-container'>
					{deviceData.map((data: DeviceData, index: number) => (
						<div className="reusable-info-container" key={index}>
							<div className="reusable-info-cell">
								{renderLabelComp(data)}

								<div className="reusable-info-value">
									{renderValuesComp(data)}
								</div>
							</div>
							{index < deviceData.length - 1 && <Divider />}
						</div>
					))}
				</CardContent>
			)}
		</Card>
	);
};